import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Landmark, Eye, Pencil, LayoutGrid, List, Lightbulb } from "lucide-react";
import TellerConnectButton from "@/components/TellerConnectButton";
import TellerSandboxBanner from "@/components/TellerSandboxBanner";
import TellerOriginBanner from "@/components/TellerOriginBanner";
import TellerReconnectBanner from "@/components/TellerReconnectBanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

const TYPES = ["checking", "savings", "credit_card", "loan"];

const BLANK = {
  name: "", bank_name: "", account_number_masked: "",
  account_type: "checking", currency: "USD", opening_balance: "",
};

// Bank account cards use a single clean white background — professional, consistent.
const CARD_TINTS = ["#FFFFFF"];
function pickRandomTint() {
  return CARD_TINTS[0];
}

/** US currency input — keeps a raw numeric string in state, displays $1,234.56 on blur. */
function CurrencyInput({ value, onChange, currency = "USD", testid }) {
  const [focused, setFocused] = useState(false);
  const numeric = typeof value === "number" ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ""));
  const display = focused
    ? value
    : (Number.isFinite(numeric)
        ? new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", currencyDisplay: "symbol" })
            .format(numeric)
        : "");
  return (
    <div className="flex items-stretch">
      <span
        className="inline-flex items-center px-2.5 text-sm font-mono-fin border border-r-0 bg-[var(--ll-bg-sidebar)] text-[var(--ll-ink-soft)]"
        style={{ borderColor: "var(--ll-border)" }}
      >
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        data-testid={testid}
        value={focused ? value : display}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          // Allow digits, minus sign, single dot
          const cleaned = e.target.value.replace(/[^0-9.\-]/g, "");
          onChange(cleaned);
        }}
        placeholder="0.00"
        className="flex-1 px-3 py-2 border font-mono-fin"
        style={{ borderColor: "var(--ll-border)" }}
      />
    </div>
  );
}

export default function BankAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("ll_accounts_view") || "grid");

  useEffect(() => { localStorage.setItem("ll_accounts_view", viewMode); }, [viewMode]);

  // Assign a random pastel tint per account, stable within the current items array.
  const tintByAccountId = useMemo(() => {
    const map = {};
    items.forEach((a) => { map[a.id] = pickRandomTint(); });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bank-accounts");
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(BLANK);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || "",
      bank_name: a.bank_name || "",
      account_number_masked: a.account_number_masked || "",
      account_type: a.account_type || "checking",
      currency: a.currency || "USD",
      opening_balance: a.opening_balance != null ? String(a.opening_balance) : "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
    try {
      if (editingId) {
        await api.patch(`/bank-accounts/${editingId}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/bank-accounts", payload);
        toast.success("Bank account added");
      }
      setOpen(false);
      setEditingId(null);
      setForm(BLANK);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this account and all related statements and transactions?")) return;
    try {
      const { data } = await api.delete(`/bank-accounts/${id}`);
      toast.success("Account removed");
      load();
      const hint = data?.reclaim;
      if (hint && Number(hint.unused || 0) > 0) setReclaimOffer(hint);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // Prompt to drop one unused bank-account add-on slot after a deletion.
  const [reclaimOffer, setReclaimOffer] = useState(null);
  const { refreshMe } = useAuth();
  const confirmReclaim = async () => {
    try {
      const { data } = await api.post("/billing/bank-accounts/reclaim");
      const saved = Number(data?.monthly_savings || 0);
      toast.success(saved > 0 ? `Reclaimed 1 slot — saving $${saved.toFixed(2)}/mo` : "Reclaimed 1 slot");
      await refreshMe?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reclaim slot");
    } finally {
      setReclaimOffer(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Bank Accounts"
        subtitle="Connected ledgers"
        testid="accounts-header"
        actions={
          <div className="flex items-center gap-2">
            <div className="inline-flex border" style={{ borderColor: "var(--ll-border)" }} data-testid="accounts-view-toggle">
              <button
                onClick={() => setViewMode("grid")}
                title="Grid view"
                data-testid="view-mode-grid"
                className={`px-2 py-2 ${viewMode === "grid" ? "bg-[var(--ll-ink)] text-white" : "bg-white hover:bg-[var(--ll-mint)]"}`}
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                title="List view"
                data-testid="view-mode-list"
                className={`px-2 py-2 border-l ${viewMode === "list" ? "bg-[var(--ll-ink)] text-white" : "bg-white hover:bg-[var(--ll-mint)]"}`}
                style={{ borderColor: "var(--ll-border)" }}
              >
                <List size={14} />
              </button>
            </div>
            <TellerConnectButton onConnected={load} />
            <button
              onClick={openCreate}
              data-testid="add-account-btn"
              className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f] transition-colors flex items-center gap-1.5"
            >
              <Plus size={14} /> Add account
            </button>
          </div>
        }
      />
      <PageBody>
        <TellerSandboxBanner />
        <TellerOriginBanner />
        <TellerReconnectBanner onReconnected={load} />
        {loading && <div className="ll-progress mb-6" />}
        {items.length === 0 && !loading && (
          <div className="ll-card-flat p-12 text-center" data-testid="accounts-empty">
            <Landmark size={32} className="mx-auto mb-3 opacity-40" />
            <div className="font-display font-bold text-xl mb-1">No bank accounts yet</div>
            <div className="text-sm text-[var(--ll-ink-soft)] mb-4">Add your first checking, savings, or credit account to start uploading statements.</div>
            <button onClick={openCreate} className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f] transition-colors" data-testid="empty-add-account-btn">
              Add your first account
            </button>
          </div>
        )}

        {items.length > 0 && viewMode === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((a) => (
              <div
                key={a.id}
                className="ll-card p-5"
                data-testid={`account-card-${a.id}`}
                style={{ backgroundColor: tintByAccountId[a.id] || "#ECFDF5" }}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{a.bank_name}</div>
                    <div className="font-display font-bold text-lg mt-0.5 truncate">{a.name}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link
                      to={`/app/transactions?bank_account_id=${a.id}`}
                      data-testid={`view-account-${a.id}`}
                      title="View transactions"
                      className="p-1.5 border bg-white hover:bg-[var(--ll-mint)] transition-colors inline-flex items-center justify-center"
                      style={{ borderColor: "rgba(5,150,105,0.4)" }}
                    >
                      <Eye size={14} />
                    </Link>
                    <button
                      onClick={() => openEdit(a)}
                      data-testid={`edit-account-${a.id}`}
                      title="Edit account"
                      className="p-1.5 border bg-white hover:bg-[var(--ll-mint)] transition-colors"
                      style={{ borderColor: "rgba(5,150,105,0.4)" }}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => del(a.id)}
                      data-testid={`delete-account-${a.id}`}
                      title="Delete account"
                      className="p-1.5 border bg-white hover:bg-[var(--ll-error)] hover:text-white hover:border-[var(--ll-error)] transition-colors"
                      style={{ borderColor: "rgba(5,150,105,0.4)" }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="font-mono-fin text-sm text-[var(--ll-ink-soft)]">{a.account_number_masked}</div>
                <div className="border-t mt-4 pt-3 flex items-center justify-between" style={{ borderColor: "rgba(5,150,105,0.2)" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Opening balance</div>
                    <div className="font-mono-fin text-base">{fmtMoney(a.opening_balance, a.currency)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Type</div>
                    <div className="text-sm">{a.account_type.replace("_", " ")}</div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-3">Added {fmtDate(a.created_at)}</div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && viewMode === "list" && (
          <div className="ll-card-flat overflow-x-auto" data-testid="accounts-list-view">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-white bg-[var(--ll-bg-sidebar)] border-b" style={{ borderColor: "var(--ll-border)" }}>
                  <th className="px-4 py-3 font-medium">Bank</th>
                  <th className="px-4 py-3 font-medium">Nickname</th>
                  <th className="px-4 py-3 font-medium">Account #</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium text-right">Opening balance</th>
                  <th className="px-4 py-3 font-medium">Added</th>
                  <th className="px-4 py-3 font-medium text-right w-32" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b hover:bg-[var(--ll-bg-sidebar)]"
                    style={{ borderColor: "var(--ll-border)", backgroundColor: tintByAccountId[a.id] || undefined }}
                    data-testid={`account-row-${a.id}`}
                  >
                    <td className="px-4 py-2.5">{a.bank_name}</td>
                    <td className="px-4 py-2.5 font-medium">{a.name}</td>
                    <td className="px-4 py-2.5 font-mono-fin text-xs">{a.account_number_masked}</td>
                    <td className="px-4 py-2.5 text-xs uppercase tracking-wider">{a.account_type.replace("_", " ")}</td>
                    <td className="px-4 py-2.5 font-mono-fin text-right">{fmtMoney(a.opening_balance, a.currency)}</td>
                    <td className="px-4 py-2.5 text-xs text-[var(--ll-ink-soft)]">{fmtDate(a.created_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/app/transactions?bank_account_id=${a.id}`}
                          data-testid={`view-account-row-${a.id}`}
                          title="View transactions"
                          className="p-1.5 border bg-white hover:bg-[var(--ll-mint)] transition-colors inline-flex items-center justify-center"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          <Eye size={13} />
                        </Link>
                        <button
                          onClick={() => openEdit(a)}
                          data-testid={`edit-account-row-${a.id}`}
                          title="Edit account"
                          className="p-1.5 border bg-white hover:bg-[var(--ll-mint)] transition-colors"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => del(a.id)}
                          data-testid={`delete-account-row-${a.id}`}
                          title="Delete account"
                          className="p-1.5 border bg-white hover:bg-[var(--ll-error)] hover:text-white hover:border-[var(--ll-error)] transition-colors"
                          style={{ borderColor: "var(--ll-border)" }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {open && (
          <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={submit}
              data-testid={editingId ? "edit-account-form" : "add-account-form"}
              className="bg-white border border-[var(--ll-ink)] w-full max-w-md p-6"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">
                {editingId ? "Update ledger" : "New ledger"}
              </div>
              <h2 className="font-display font-bold text-2xl mb-5">
                {editingId ? "Edit bank account" : "Add bank account"}
              </h2>
              <div className="space-y-3">
                {[
                  { k: "name", label: "Nickname", placeholder: "Operating Checking", testid: "form-name" },
                  { k: "bank_name", label: "Bank name", placeholder: "Chase Bank", testid: "form-bank" },
                  { k: "account_number_masked", label: "Account number (masked)", placeholder: "•••• 4567", testid: "form-account" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">{f.label}</label>
                    <input
                      required value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      placeholder={f.placeholder} data-testid={f.testid}
                      className="w-full px-3 py-2 border" style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Type</label>
                    <select
                      value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                      data-testid="form-type"
                      className="w-full px-3 py-2 border bg-white" style={{ borderColor: "var(--ll-border)" }}
                    >
                      {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Currency</label>
                    <input
                      value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                      data-testid="form-currency"
                      className="w-full px-3 py-2 border uppercase" style={{ borderColor: "var(--ll-border)" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Opening balance (USD)</label>
                  <CurrencyInput
                    value={form.opening_balance}
                    onChange={(v) => setForm({ ...form, opening_balance: v })}
                    currency={form.currency}
                    testid="form-opening"
                  />
                  {editingId && (
                    <OpeningBalanceSuggestion
                      accountId={editingId}
                      currentDraft={form.opening_balance}
                      onApply={(v) => setForm({ ...form, opening_balance: String(v) })}
                    />
                  )}
                  <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1">
                    Enter a number; we'll format it as US currency when you leave the field.
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-6">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border" style={{ borderColor: "var(--ll-border)" }} data-testid="form-cancel">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-[var(--ll-ink)] text-white" data-testid="form-submit">
                  {editingId ? "Save changes" : "Save account"}
                </button>
              </div>
            </form>
          </div>
        )}
      </PageBody>

      {/* Reclaim unused bank-account slot prompt */}
      <Dialog open={!!reclaimOffer} onOpenChange={(o) => !o && setReclaimOffer(null)}>
        <DialogContent className="max-w-sm" data-testid="reclaim-bank-slot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Drop an empty bank slot?</DialogTitle>
            <DialogDescription className="text-[var(--ll-ink-soft)]">
              You now have {reclaimOffer?.unused || 0} paid bank-account slot{(reclaimOffer?.unused || 0) === 1 ? "" : "s"} nothing is using.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 text-sm text-[var(--ll-ink)]">
            Reclaim one slot to save <strong>${Number(reclaimOffer?.monthly_savings_per_slot || 0).toFixed(2)}/mo</strong>. You can buy it back later from Settings → Workspace.
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setReclaimOffer(null)}
              data-testid="reclaim-bank-dialog-dismiss"
              className="flex-1 px-4 py-2 border text-sm"
              style={{ borderColor: "var(--ll-border)" }}
            >
              Keep slot
            </button>
            <button
              type="button"
              onClick={confirmReclaim}
              data-testid="reclaim-bank-dialog-confirm"
              className="flex-1 px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] text-sm"
            >
              Drop 1 slot
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Suggestion widget shown inside the edit form. Lazily peeks at the
 * earliest parsed statement for this account and offers a one-click
 * "Use suggestion" button. Hidden when the user is still typing the same
 * value, when there's no parsed statement yet, or when the suggestion
 * equals what's already in the form.
 */
function OpeningBalanceSuggestion({ accountId, currentDraft, onApply }) {
  const [data, setData] = useState(null);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/bank-accounts/${accountId}/opening-balance-suggestion`);
        setData(r.data);
      } catch { /* no statements yet — don't show the widget */ }
      finally { setTried(true); }
    })();
  }, [accountId]);
  if (!tried) return null;
  if (!data || data.suggested == null) return null;
  const draftNum = parseFloat(String(currentDraft).replace(/,/g, ""));
  if (Number.isFinite(draftNum) && Math.abs(draftNum - data.suggested) < 0.005) return null;
  return (
    <button
      type="button"
      onClick={() => onApply(data.suggested)}
      data-testid="form-opening-suggestion"
      className="mt-2 w-full flex items-center justify-between gap-2 text-xs px-2 py-1.5 border bg-[var(--ll-mint)]/40 hover:bg-[var(--ll-mint)]"
      style={{ borderColor: "var(--ll-border)" }}
      title={`Pulled from ${data.source_file_name || "the earliest statement"}${data.period_start ? ` (${data.period_start})` : ""}`}
    >
      <span className="flex items-center gap-1.5">
        <Lightbulb size={12} className="text-[var(--ll-ochre-ink)]" />
        Use statement&apos;s opening balance
      </span>
      <span className="font-mono-fin">{fmtMoney(data.suggested)}</span>
    </button>
  );
}

